import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../common/Modal';
import Select from '../../common/Select';
import Input from '../../common/Input';
import Button from '../../common/Button';
import { TICKET_TYPES, TICKET_TYPE_LABELS, TYPES_REQUIRING_NO_SELECTION } from '../../../constants/statuses';
import { validateRequired } from '../../../utils/validators';
import systemTypeService from '../../../services/systemTypeService';
import siteService from '../../../services/siteService';

const TLEditTicketModal = ({ isOpen, onClose, onSubmit, ticket }) => {
  const [formData, setFormData] = useState({
    system_type: '',
    problem_sub_type: '',
    custom_problem: '',
    ticket_type: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [activeContract, setActiveContract] = useState(null);
  const [loadingContract, setLoadingContract] = useState(false);
  const [isSystemTypeChanged, setIsSystemTypeChanged] = useState(false);
  const [systemTroubles, setSystemTroubles] = useState({});

  useEffect(() => {
  systemTypeService.getProblemMapping().then(result => {
    if (result.success) setSystemTroubles(result.data);
  });
  }, []);

  useEffect(() => {
    if (!ticket) {
      return;
    }

    const predefinedList = systemTroubles[ticket.system_type] || [];
    const isCustom = ticket.problem_type && !predefinedList.includes(ticket.problem_type);

    setFormData({
      system_type: ticket.system_type || '',
      problem_sub_type: isCustom ? 'Other' : (ticket.problem_type || ''),
      custom_problem: isCustom ? ticket.problem_type : '',
      ticket_type: ticket.ticket_type || ''
    });
    setIsSystemTypeChanged(false);
    setErrors({});
  }, [ticket]);

  // Fetch the active contract for the ticket's site to filter allowed system types
  useEffect(() => {
    const fetchContract = async () => {
      if (!ticket?.site_id) {
        setActiveContract(null);
        return;
      }
      setLoadingContract(true);
      try {
        const response = await siteService.getSiteContracts(ticket.site_id);
        if (response.success) {
          const active = response.data.find(c => c.is_enabled);
          setActiveContract(active || null);
        } else {
          setActiveContract(null);
        }
      } catch (err) {
        console.error('Error fetching contract for filtering:', err);
        setActiveContract(null);
      } finally {
        setLoadingContract(false);
      }
    };
    fetchContract();
  }, [ticket?.site_id]);

  const problemTypeOptions = useMemo(() => {
    const options = Object.keys(systemTroubles)
      .filter(type => {
        if (!activeContract || !activeContract.services) return false;
        const services = activeContract.services;
        if (Array.isArray(services)) return services.includes(type);
        return (services.comprehensive || []).includes(type) || (services.non_comprehensive || []).includes(type);
      })
      .map((value) => ({ value, label: value }));

    // Preserve the ticket's current value even if it's outside the contract
    if (formData.system_type && !options.find(o => o.value === formData.system_type)) {
      options.push({ value: formData.system_type, label: formData.system_type });
    }
    return options;
  }, [formData.system_type, activeContract]);

  const problemSubTypeOptions = useMemo(() => {
    const predefined = systemTroubles[formData.system_type] || [];
    const options = predefined.map((value) => ({ value, label: value }));
    
    if (formData.problem_sub_type && formData.problem_sub_type !== 'Other' && !predefined.includes(formData.problem_sub_type)) {
       options.push({ value: formData.problem_sub_type, label: formData.problem_sub_type });
    }
    
    options.push({ value: 'Other', label: 'Other (Specify)' });
    return options;
  }, [formData.system_type, formData.problem_sub_type]);

  const ticketTypeOptions = useMemo(
    () => Object.values(TICKET_TYPES).map((value) => ({ value, label: TICKET_TYPE_LABELS[value] || value })),
    []
  );

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      
      if (name === 'ticket_type') {
        const wasPM = TYPES_REQUIRING_NO_SELECTION.includes(prev.ticket_type);
        const isNowPM = TYPES_REQUIRING_NO_SELECTION.includes(value);
        if (wasPM !== isNowPM) {
          setIsSystemTypeChanged(true);
          updated.system_type = '';
          updated.problem_sub_type = '';
          updated.custom_problem = '';
        }
      }

      if (name === 'system_type') {
        setIsSystemTypeChanged(true);
        updated.problem_sub_type = '';
        updated.custom_problem = '';
      }
      
      if (name === 'problem_sub_type' && value !== 'Other') {
        updated.custom_problem = '';
      }
      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const nextErrors = {};

    const isPM = TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type);

    if (isSystemTypeChanged && !isPM) {
      if (!validateRequired(formData.system_type)) {
        nextErrors.system_type = 'System type is required';
      }
      if (!validateRequired(formData.problem_sub_type)) {
        nextErrors.problem_sub_type = 'Problem sub-type is required';
      } else if (formData.problem_sub_type === 'Other' && !validateRequired(formData.custom_problem)) {
        nextErrors.custom_problem = 'Please specify your custom problem';
      }
    }
    
    if (!validateRequired(formData.ticket_type)) {
      nextErrors.ticket_type = 'Ticket type is required';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!ticket?.id || !validate()) {
      return;
    }

    setSubmitting(true);
    try {
      const isPM = TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type);
      const updateData = { ticket_type: formData.ticket_type };

      if (isPM) {
        updateData.system_type = ticket?.system_type || 'Preventive Maintenance';
        updateData.problem_type = ticket?.problem_type || 'Scheduled PM';
      } else if (isSystemTypeChanged) {
        const finalSubType = formData.problem_sub_type === 'Other' ? formData.custom_problem : formData.problem_sub_type;
        updateData.system_type = formData.system_type;
        updateData.problem_type = finalSubType;
      }

      await onSubmit(ticket.id, updateData);
      onClose();
    } catch (submitError) {
      console.error('Failed to update ticket:', submitError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit Ticket #${ticket?.id || ''}`}
      size="medium"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Updating...' : 'Update Ticket'}
          </Button>
        </>
      )}
    >
      <Select
        label={loadingContract ? "Loading allowed systems..." : "Problem Type"}
        name="system_type"
        value={formData.system_type}
        onChange={handleChange}
        options={problemTypeOptions}
        error={errors.system_type}
        required={!TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type)}
        disabled={loadingContract || !activeContract || TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type)}
        placeholder={TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type) ? "-- Not required for PM --" : (!activeContract && !loadingContract ? "No active contract found" : "Select problem type")}
      />

      {(!isSystemTypeChanged && formData.problem_sub_type !== 'Other') ? (
        <Input
          label="Problem Sub-Type"
          name="problem_sub_type_readonly"
          value={formData.problem_sub_type || 'No Sub-Type specified'}
          disabled
        />
      ) : (
        <Select
          label="Problem Sub-Type"
          name="problem_sub_type"
          value={formData.problem_sub_type}
          onChange={handleChange}
          options={problemSubTypeOptions}
          error={errors.problem_sub_type}
          required={isSystemTypeChanged}
          disabled={!formData.system_type || (!isSystemTypeChanged && formData.problem_sub_type !== 'Other')}
          placeholder={formData.system_type ? 'Select problem sub-type' : 'Select problem type first'}
        />
      )}

      {formData.problem_sub_type === 'Other' && (
        <Input
          label="Specify Custom Problem"
          name="custom_problem"
          placeholder="Enter specific problem details"
          value={formData.custom_problem}
          onChange={handleChange}
          error={errors.custom_problem}
          required
        />
      )}

      <Select
        label="Ticket Type"
        name="ticket_type"
        value={formData.ticket_type}
        onChange={handleChange}
        options={ticketTypeOptions}
        error={errors.ticket_type}
        required
        placeholder="Select ticket type"
      />
    </Modal>
  );
};

export default TLEditTicketModal;
